import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuth0 } from '@auth0/auth0-react';
import CharacterBrowser from './components/CharacterBrowser';
import CharacterForm from './components/CharacterForm';
import EditCharacter from './components/EditCharacter';
import AccountSettings from './components/AccountSettings';
import UserManagement from './components/UserManagement';
import DevFooter from './components/DevFooter';
import LandingPage from './components/LandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import RoadmapBugs from './components/RoadmapBugs';
import config from './config';
import AdminCharacterList from './components/AdminCharacterList';
import MyCharacters from './components/MyCharacters';
import axios from 'axios';
import { initGA } from './utils/analytics';
import RoadmapEdit from './components/RoadmapEdit';

const Footer: React.FC = () => (
  <Box 
    component="footer" 
    sx={{ 
      py: 3, 
      px: 2, 
      mt: 'auto',
      backgroundColor: 'background.paper',
      borderTop: 1,
      borderColor: 'divider'
    }}
  >
    <Typography variant="body2" color="text.secondary" align="center">
      <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>
        Privacy Policy
      </Link>
    </Typography>
  </Box>
);

const App: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [counts, setCounts] = useState<{ 
    characterCount: number; 
    userCount: number; 
    guildCount: number;
    myCharacterCount: number;
  }>({ 
    characterCount: 0, 
    userCount: 0, 
    guildCount: 0,
    myCharacterCount: 0
  });

  // Handle legacy URLs by redirecting to hash routes
  useEffect(() => {
    const currentUrl = window.location.href;
    const baseUrl = window.location.origin;
    const hashIndex = currentUrl.indexOf('#');

    // If the URL doesn't already include a hash and isn't the base URL, redirect
    if (hashIndex === -1 && currentUrl !== `${baseUrl}/`) {
      const path = currentUrl.replace(baseUrl, '');
      window.location.replace(`${baseUrl}/#${path}`);
    }
  }, []);

  // Initialize Google Analytics
  useEffect(() => {
    initGA();
  }, []);

  // Fetch counts
  useEffect(() => {
    const fetchCounts = async () => {
      if (!isAuthenticated) return;
      
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: config.auth0.audience,
            scope: 'openid profile email'
          }
        });
        const response = await axios.get(`${config.api.baseURL}/api/characters/counts`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setCounts(response.data);
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    fetchCounts();

    // Add event listeners for character and guild changes
    const handleCharacterAdded = () => {
      fetchCounts();
    };
    const handleGuildAdded = () => {
      fetchCounts();
    };
    window.addEventListener('character-added', handleCharacterAdded);
    window.addEventListener('guild-added', handleGuildAdded);

    // Cleanup
    return () => {
      window.removeEventListener('character-added', handleCharacterAdded);
      window.removeEventListener('guild-added', handleGuildAdded);
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (user && !localStorage.getItem(`hasSeenWelcome_${user.sub}`)) {
      setShowWelcomeDialog(true);
    }
  }, [user]);

  const handleWelcomeClose = () => {
    setShowWelcomeDialog(false);
    if (user) {
      localStorage.setItem(`hasSeenWelcome_${user.sub}`, 'true');
    }
  };

  const handleAccountClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsClick = () => {
    handleAccountClose();
    setIsSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setIsSettingsOpen(false);
  };

  const handleLogout = () => {
    handleAccountClose();
    if (config.auth0.enabled) {
      logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
      console.log('Logout clicked (disabled in development)');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Handle public routes first
  if (window.location.pathname === '/privacy') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh' 
      }}>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh' 
      }}>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh' 
    }}>
      <AppBar position="static">
        <Toolbar>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ mr: 4, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            EQ Emblem
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              component={Link}
              to="/"
              color="inherit"
            >
              Character Browser
            </Button>
            <Button
              component={Link}
              to="/my-characters"
              color="inherit"
            >
              My Characters
            </Button>
            <Button
              component={Link}
              to="/roadmap"
              color="inherit"
            >
              Roadmap/Bugs
            </Button>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {user?.name && (
            <Typography 
              color="inherit" 
              sx={{ mr: 2 }}
            >
              {user.name}
            </Typography>
          )}
          <IconButton
            size="large"
            color="inherit"
            onClick={handleAccountClick}
          >
            <AccountCircleIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleAccountClose}
          >
            <MenuItem onClick={handleSettingsClick}>Account Settings</MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box 
        sx={{ 
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          mb: 4,
          py: 1.5
        }}
      >
        <Box sx={{ 
          maxWidth: 'lg',
          mx: 'auto',
          px: 3,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          gap: 4
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
              {counts.userCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Users
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
              {counts.characterCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Characters
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
              {counts.guildCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Guilds
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
              {counts.myCharacterCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              My Characters
            </Typography>
          </Box>
        </Box>
      </Box>

      <AccountSettings 
        open={isSettingsOpen}
        onClose={handleSettingsClose}
      />

      <AccountSettings 
        open={showWelcomeDialog}
        onClose={handleWelcomeClose}
        isWelcomeDialog={true}
      />

      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2, pb: 8, flex: 1 }}>
        <Routes>
          <Route path="/" element={<CharacterBrowser />} />
          <Route path="/my-characters" element={<MyCharacters />} />
          <Route path="/add-character" element={<CharacterForm />} />
          <Route path="/edit-character" element={<EditCharacter />} />
          <Route path="/dev/users" element={<UserManagement />} />
          <Route path="/dev/characters" element={<AdminCharacterList />} />
          <Route path="/roadmap" element={<RoadmapBugs />} />
          <Route path="/dev/roadmap" element={<RoadmapEdit />} />
        </Routes>
      </Box>

      <Footer />
      <DevFooter />
    </Box>
  );
};

export default App; 